import React, { FC } from 'react'
import { useTheme } from 'styled-components'

// Icons
import { ReactComponent as DocumentProcessing } from '@sketch/icons/document-processing-64'

// Hooks
import { useCanvasPresentationFile } from 'components/WebRendererCanvas/hooks'

// Components
import LoadingState from 'modules/shares/components/LoadingState'
import { ErrorMessage } from '@sketch/components'
import { PageCanvasError } from 'modules/shares/PageCanvasView/components/PageCanvasError'
import { PageAnnotationsOverlay } from 'modules/annotations/components'
import { WebRendererCanvas } from 'components/WebRendererCanvas'

// Local Hooks
import {
  useInitialCameraPosition,
  useSaveLastCameraPositionInLocalStorage,
} from '../../hooks'

// Local Components
import CanvasContextMenu, { useCanvasContextMenu } from '../CanvasContextMenu'
import PageOriginContent from '../PageOriginContent'
import { ArtboardsInfoOverlay } from '../ArtboardsInfoOverlay'

// Styles
import { PageCanvasWrapper, StyledPageCanvas } from './SharePageCanvas.styles'
import { useCanvasAnnotations } from 'modules/annotations/hooks'

interface SharePageCanvasProps {
  permanentPageIdentifier: string
}

export const SharePageCanvas: FC<SharePageCanvasProps> = ({
  permanentPageIdentifier: pageUUID,
}) => {
  const theme = useTheme()

  const {
    share,
    page,
    versionShortId,
    isRendering,
    hasError,
    isLoading,
  } = useCanvasPresentationFile({
    pageUUID,
  })

  const filePath = page?.presentationFile?.downloadUrl

  // Camera Position
  const initialCameraPosition = useInitialCameraPosition({
    pageUUID,
    versionShortId,
  })

  const { initialZoom, initialPan } = initialCameraPosition

  useSaveLastCameraPositionInLocalStorage({ pageUUID, versionShortId })

  // Context Menu
  const {
    contextMenuPos,
    contextMenuMetaData,
    canvasContainerRef,
    handleContextMenu,
    handleCloseContextMenu,
  } = useCanvasContextMenu()

  // Annotations
  const {
    activeAnnotation,
    isPlacingDraftAnnotation,
    ...pointerEvents
  } = useCanvasAnnotations({
    shareIdentifier: share.identifier,
    permanentPageId: pageUUID,
  })

  if (isLoading) {
    return <LoadingState />
  }

  if (isRendering) {
    return (
      <ErrorMessage
        title="Rendering Canvas…"
        description="The canvas is rendering. When it’s ready, it will appear here."
        icon={<DocumentProcessing />}
      />
    )
  }

  /**
   * By this point filePath shouldn't ever be undefined, but we narrow the type
   * to a defined string with this error case just to be sure.
   */
  if (!filePath) {
    return (
      <PageCanvasError
        description={
          <>
            Something went wrong while loading the canvas. Please reload — or
            try again later.
          </>
        }
        showReloadCanvasAction
      />
    )
  }

  /**
   * This error condition is a relic from the early MVP of the canvas that still
   * used the legacy JSON format. As such only internal Sketch users that may
   * still have a JSON PR file associated with their Sketch document will ever
   * see it. Safe to remove in future.
   */
  if (filePath.includes('.json')) {
    return (
      <PageCanvasError
        description={
          <>
            Please save a new document version in Sketch and reload to see this
            page as a canvas.
          </>
        }
        showReloadCanvasAction
      />
    )
  }

  if (hasError || !page) {
    return (
      <PageCanvasError
        description={
          <>
            There was a problem fetching the page data. You can find more
            information on our{' '}
            <a href="https://status.sketch.com/">Status Page</a>.
          </>
        }
        showReloadCanvasAction
      />
    )
  }

  return (
    <PageCanvasWrapper>
      <StyledPageCanvas ref={canvasContainerRef}>
        <WebRendererCanvas
          pageUUID={pageUUID}
          page={page}
          versionShortId={versionShortId}
          shareIdentifier={share.identifier}
          initialZoom={initialZoom}
          initialPan={initialPan}
          backgroundColor={theme.colors.background.secondary.B}
          webRenderCursor={isPlacingDraftAnnotation ? 'copy' : undefined}
          webRenderContainerProps={{
            onContextMenu: handleContextMenu,
            ...pointerEvents,
          }}
        >
          <PageOriginContent>
            <ArtboardsInfoOverlay
              disablePointerCursor={isPlacingDraftAnnotation}
            />
            <PageAnnotationsOverlay
              shareIdentifier={share.identifier}
              permanentPageId={pageUUID}
              activeAnnotationIdentifier={activeAnnotation}
              parentWrapper={canvasContainerRef.current || undefined}
            />
          </PageOriginContent>

          <CanvasContextMenu
            permanentPageId={pageUUID}
            closeTooltip={handleCloseContextMenu}
            contextMenuPos={contextMenuPos}
            contextMenuMetaData={contextMenuMetaData}
          />
        </WebRendererCanvas>
      </StyledPageCanvas>
    </PageCanvasWrapper>
  )
}
